export const KIDS_CHURCH_CLASSES = [
  { id: '0-2', label: '0yr - 2yr', minAge: 0, maxAge: 2 },
  { id: '3-5', label: '3yr - 5yr', minAge: 3, maxAge: 5 },
  { id: '6-12', label: '6yr - 12yr', minAge: 6, maxAge: 12 },
  { id: '13-up', label: '13yr - Up', minAge: 13, maxAge: null },
] as const;

export type KidsChurchClassId = (typeof KIDS_CHURCH_CLASSES)[number]['id'];

export type KidsChurchClassDateRange = {
  /** Latest date of birth included. The child has reached the band's minimum age. */
  maxDob: string;
  /** Dates of birth on or before this day are too old for the band. */
  minDobExclusive?: string;
};

const EMPTY_CLASS_LABEL = '—';

export function isKidsChurchClassId(value: string): value is KidsChurchClassId {
  return KIDS_CHURCH_CLASSES.some((item) => item.id === value);
}

function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function subtractYears(date: Date, years: number): string {
  const year = date.getFullYear() - years;
  const month = date.getMonth();
  const day = date.getDate();
  const candidate = new Date(year, month, day);
  if (candidate.getMonth() !== month) {
    return toLocalIsoDate(new Date(year, month + 1, 0));
  }
  return toLocalIsoDate(candidate);
}

export function getKidsChurchClassDateRange(
  classId: KidsChurchClassId,
  today = new Date()
): KidsChurchClassDateRange | null {
  const band = KIDS_CHURCH_CLASSES.find((item) => item.id === classId);
  if (!band) return null;

  const todayDate = startOfLocalDay(today);
  const maxDob = subtractYears(todayDate, band.minAge);
  if (band.maxAge == null) {
    return { maxDob };
  }

  return {
    maxDob,
    minDobExclusive: subtractYears(todayDate, band.maxAge + 1),
  };
}

export function getKidsChurchClass(dateOfBirth?: string | null, today = new Date()): string {
  if (!dateOfBirth) return EMPTY_CLASS_LABEL;
  const dob = parseIsoDate(dateOfBirth);
  if (!dob) return EMPTY_CLASS_LABEL;

  const dobIso = toLocalIsoDate(dob);
  const todayIso = toLocalIsoDate(startOfLocalDay(today));
  if (dobIso > todayIso) return EMPTY_CLASS_LABEL;

  for (const band of KIDS_CHURCH_CLASSES) {
    const range = getKidsChurchClassDateRange(band.id, today);
    if (!range) continue;
    const hasReachedMinAge = dobIso <= range.maxDob;
    const hasNotExceededMaxAge = range.minDobExclusive ? dobIso > range.minDobExclusive : true;
    if (hasReachedMinAge && hasNotExceededMaxAge) return band.label;
  }

  return EMPTY_CLASS_LABEL;
}
